import {
  Box,
  CircularProgress,
  Divider,
  InputAdornment,
  List,
  Popover,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useMemo, useState } from "react";
import { useDebouncedSearch } from "shared-ui/src/utils/useDebouncedSearch";

export interface SearchPopoverState {
  search: string;
  processedSearch: string;
  debouncedSearch: string;
  shouldHide: boolean;
  isDebouncePending: boolean;
}

interface SearchPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  minSearchLength?: number;
  debounceMs?: number;
  resetSearchOnClose?: boolean;
  onDebouncedSearchChange?: (value: string) => void;
  onSearchKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  searchPlaceholder: string;
  searchInputRef?: React.Ref<HTMLInputElement>;
  searchInputName?: string;
  searchInputAriaLabel?: string;
  isSearching?: boolean | ((state: SearchPopoverState) => boolean);
  searchTelemetryProps?: Record<string, string>;
  listId?: string;
  listAriaLabel?: string;
  listRef?: React.Ref<HTMLUListElement>;
  statusMessage?: string | ((state: SearchPopoverState) => string);
  statusSx?: SxProps<Theme>;
  isEmpty?: boolean | ((state: SearchPopoverState) => boolean);
  emptyMessage?: string | ((state: SearchPopoverState) => string);
  children: React.ReactNode | ((state: SearchPopoverState) => React.ReactNode);
}

export default function SearchPopover({
  anchorEl,
  open,
  onClose,
  minSearchLength = 3,
  debounceMs = 1000,
  resetSearchOnClose = true,
  onDebouncedSearchChange,
  onSearchKeyDown,
  searchPlaceholder,
  searchInputRef,
  searchInputName,
  searchInputAriaLabel,
  isSearching = false,
  searchTelemetryProps,
  listId,
  listAriaLabel,
  listRef,
  statusMessage,
  statusSx,
  isEmpty = false,
  emptyMessage,
  children,
}: SearchPopoverProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(search, debounceMs);

  useEffect(() => {
    if (!resetSearchOnClose || open) {
      return;
    }

    setSearch("");
  }, [open, resetSearchOnClose]);

  useEffect(() => {
    if (!onDebouncedSearchChange) {
      return;
    }

    if (
      debouncedSearch.length >= minSearchLength ||
      debouncedSearch.length === 0
    ) {
      onDebouncedSearchChange(debouncedSearch);
    }
  }, [debouncedSearch, minSearchLength, onDebouncedSearchChange]);

  const state = useMemo(() => {
    const processedSearch = search.replace(/ {2,}/g, " ").trim();
    const shouldHide =
      processedSearch.length > 0 && processedSearch.length < minSearchLength;
    const isDebouncePending =
      processedSearch.length >= minSearchLength &&
      processedSearch !== debouncedSearch;

    return {
      search,
      processedSearch,
      debouncedSearch,
      shouldHide,
      isDebouncePending,
    } satisfies SearchPopoverState;
  }, [debouncedSearch, minSearchLength, search]);

  const resolvedIsSearching =
    typeof isSearching === "function" ? isSearching(state) : isSearching;
  const resolvedStatusMessage =
    typeof statusMessage === "function" ? statusMessage(state) : statusMessage;
  const resolvedIsEmpty =
    typeof isEmpty === "function" ? isEmpty(state) : isEmpty;
  const resolvedEmptyMessage =
    typeof emptyMessage === "function" ? emptyMessage(state) : emptyMessage;
  const renderedChildren =
    typeof children === "function" ? children(state) : children;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            width: {
              xs: (theme) => `min(92vw, ${theme.spacing(45)})`,
              sm: (theme) => theme.spacing(45),
            },
            maxHeight: {
              xs: "60vh",
              sm: (theme) => theme.spacing(52.5),
            },
            display: "flex",
            flexDirection: "column",
            borderRadius: 1,
            // Keep dropdown readable in both light and dark themes.
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : theme.palette.common.white,
            color: "text.primary",
            border: 1,
            borderColor: "divider",
          },
        },
      }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          inputRef={searchInputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={searchPlaceholder}
          {...searchTelemetryProps}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: resolvedIsSearching ? (
                <InputAdornment position="end">
                  <CircularProgress
                    color="inherit"
                    size="1rem"
                    aria-label="Searching"
                  />
                </InputAdornment>
              ) : undefined,
            },
            htmlInput: {
              "aria-label": searchInputAriaLabel ?? searchPlaceholder,
              "aria-busy": resolvedIsSearching,
              autoComplete: "off",
              name: searchInputName,
            },
          }}
        />
      </Box>
      <Divider />
      {resolvedStatusMessage ? (
        <Box role="status" aria-live="polite" sx={statusSx}>
          {resolvedStatusMessage}
        </Box>
      ) : null}
      <List
        dense
        disablePadding
        role="listbox"
        id={listId}
        aria-label={listAriaLabel}
        ref={listRef}
        sx={{
          overflowY: "auto",
          flex: 1,
          "& .MuiListItemButton-root": {
            color: "text.primary",
            transition: "background-color 150ms ease, color 150ms ease",
            "&:hover": {
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.06)"
                  : "action.hover",
            },
            "&.Mui-selected": {
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(125, 211, 252, 0.18)"
                  : "action.selected",
              color: "text.primary",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(125, 211, 252, 0.24)"
                    : "action.selected",
              },
            },
          },
        }}
      >
        {resolvedIsEmpty && resolvedEmptyMessage ? (
          <Box sx={{ px: 2, py: 1.5 }} aria-hidden="true">
            <Typography variant="body2" color="text.secondary">
              {resolvedEmptyMessage}
            </Typography>
          </Box>
        ) : (
          renderedChildren
        )}
      </List>
    </Popover>
  );
}
